import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import Navbar from "@/components/Navbar";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Package, Truck, Users, DollarSign, Plus, Save, RefreshCw, FileText, FileSpreadsheet, ExternalLink, LayoutGrid, Table as TableIcon, Search, Mail, Trash2, Download } from "lucide-react";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import QuotesTab from "@/components/admin/QuotesTab";
import OrdersBoard from "@/components/admin/OrdersBoard";
import DatePickerAU from "@/components/DatePickerAU";
import ImpersonateSwitch from "@/components/admin/ImpersonateSwitch";
import CustomerDetailsDialog, { downloadText } from "@/components/admin/CustomerDetailsDialog";
import XeroContactsCard from "@/components/admin/XeroContactsCard";
import { StripePayoutsCard } from "@/components/admin/StripePayoutsCard";
import { AccountManifestDialog } from "@/components/admin/AccountManifestDialog";
import { BranchTransferCard } from "@/components/admin/BranchTransferCard";
import { serviceLabel } from "@/components/booking/constants";
import BusinessesTab from "@/components/admin/BusinessesTab";
import PaintDeliveryStatus from "@/components/PaintDeliveryStatus";
import { useAuth } from "@/context/AuthContext";
import RecapCard from "@/components/admin/RecapCard";
import ArchiveTab from "@/components/admin/ArchiveTab";
import InsightsTab from "@/components/admin/InsightsTab";
import PaintTab from "@/components/admin/PaintTab";
import AddressesTab from "@/components/admin/AddressesTab";
import CancelOrderDialog from "@/components/CancelOrderDialog";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-extrabold tracking-tight text-kilted-green">Operations dashboard</h1>
            <p className="mt-1 text-sm text-muted-foreground">Manage orders, drivers, customers, pricing and reports.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <ImpersonateSwitch />
            <Button variant="outline" onClick={() => navigate("/manifest")} data-testid="admin-manifest-button" className="gap-1.5 border-kilted-green text-kilted-green hover:bg-kilted-green/10">
              Scan manifest
            </Button>
            <Button onClick={() => navigate("/quotes/new")} data-testid="admin-new-quote-button" className="gap-1.5 bg-kilted-gold text-kilted-green hover:bg-kilted-goldLight">
              <FileText className="h-4 w-4" /> New quote
            </Button>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="admin-stats">
          <Stat icon={DollarSign} label="Revenue" value={stats ? `$${stats.revenue.toFixed(2)}` : "…"} accent />
          <Stat icon={Package} label="Active orders" value={stats?.active_orders ?? "…"} />
          <Stat icon={Truck} label="Drivers" value={stats?.drivers ?? "…"} />
          <Stat icon={Users} label="Customers" value={stats?.customers ?? "…"} />
        </div>

        <RecapCard />

        <Tabs defaultValue="orders" className="mt-8">
          <div className="max-w-full overflow-x-auto">
            <TabsList data-testid="admin-tabs" className="h-auto w-full flex-wrap justify-start gap-1">
              <TabsTrigger value="orders" data-testid="tab-orders">Orders</TabsTrigger>
            <TabsTrigger value="drivers" data-testid="tab-drivers">Drivers</TabsTrigger>
            <TabsTrigger value="customers" data-testid="tab-customers">Customers</TabsTrigger>
            <TabsTrigger value="pricing" data-testid="tab-pricing">Pricing</TabsTrigger>
            <TabsTrigger value="accounts" data-testid="tab-accounts">Accounts</TabsTrigger>
            <TabsTrigger value="stripe" data-testid="tab-stripe">Stripe</TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports">Reports</TabsTrigger>
            <TabsTrigger value="insights" data-testid="tab-insights">Insights</TabsTrigger>
            <TabsTrigger value="referrals" data-testid="tab-referrals">Referrals</TabsTrigger>
            <TabsTrigger value="quotes" data-testid="tab-quotes">Quotes</TabsTrigger>
            <TabsTrigger value="requests" data-testid="tab-requests">Requests</TabsTrigger>
            <TabsTrigger value="paint" data-testid="tab-paint">Paint</TabsTrigger>
            <TabsTrigger value="businesses" data-testid="tab-businesses">Businesses</TabsTrigger>
            <TabsTrigger value="addresses" data-testid="tab-addresses">Addresses</TabsTrigger>
            <TabsTrigger value="archive" data-testid="tab-archive">Archive</TabsTrigger>
          </TabsList>
          </div>
          <TabsContent value="orders"><OrdersTab /></TabsContent>
          <TabsContent value="drivers"><DriversTab /></TabsContent>
          <TabsContent value="customers"><CustomersTab /></TabsContent>
          <TabsContent value="pricing"><PricingTab /></TabsContent>
          <TabsContent value="archive"><ArchiveTab /></TabsContent>
          <TabsContent value="accounts"><AccountsTab /></TabsContent>
          <TabsContent value="stripe"><StripeSummaryTab /></TabsContent>
          <TabsContent value="reports"><ReportsTab /></TabsContent>
          <TabsContent value="insights"><InsightsTab /></TabsContent>
          <TabsContent value="referrals"><ReferralsTab /></TabsContent>
          <TabsContent value="quotes"><QuotesTab /></TabsContent>
          <TabsContent value="requests"><RequestsTab /></TabsContent>
          <TabsContent value="paint"><PaintTab /></TabsContent>
          <TabsContent value="businesses"><BusinessesTab /></TabsContent>
          <TabsContent value="addresses"><AddressesTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <Card className={accent ? "border-kilted-gold/40 bg-kilted-gold/5" : ""}>
      <CardContent className="flex items-center gap-3 p-4">
        <div className={`flex h-11 w-11 items-center justify-center rounded-md ${accent ? "bg-kilted-gold text-kilted-green" : "bg-kilted-green text-kilted-gold"}`}><Icon className="h-5 w-5" /></div>
        <div>
          <div className="text-xs font-medium uppercase text-muted-foreground">{label}</div>
          <div className="font-heading text-2xl font-extrabold text-foreground">{value}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function OrdersTab() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [orders, setOrders] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [view, setView] = useState(() => localStorage.getItem("kc-orders-view") || "board");
  const [runDate, setRunDate] = useState(() => new Date().toLocaleDateString("sv-SE"));
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState("");

  const [paintView, setPaintView] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const quickStatus = async (order, status, label) => {
    try {
      await api.post(`/admin/orders/${order.id}/set-status`, { status, note: "" });
      toast.success(`${order.tracking_number} → ${label}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const [paint, setPaint] = useState([]);
  const load = useCallback(() => Promise.all([
    api.get("/admin/orders").then((r) => setOrders(r.data)),
    api.get("/paint/admin/board").then((r) => setPaint(r.data)).catch(() => setPaint([])),
  ]), []);
  useEffect(() => { load(); api.get("/admin/drivers").then((r) => setDrivers(r.data)); }, [load]);

  // Paint orders ride the same board as consignments (Paid → run lanes → delivered)
  const paintBubbles = paint.map((p) => ({
    id: p.id, kind: "paint", paint: p,
    tracking_number: `PAINT-${p.id.slice(0, 6).toUpperCase()}`,
    customer_name: `${p.shop_name} → ${p.customer?.name || ""}`,
    pickup: { city: "Cooma" }, dropoff: { city: p.customer?.city || p.customer?.address || "", line1: p.customer?.address || "", lat: p.customer?.lat, lng: p.customer?.lng },
    price: { total: p.total || 0 }, billing: "account", payment: { status: "on_account" },
    status: p.delivered_at ? "delivered" : p.driver_id ? "assigned" : "confirmed",
    delivery_run: p.delivery_run || null, run_date: p.run_date || null, run_sequence: p.run_sequence ?? null,
    driver_name: p.driver_name || null, created_at: p.created_at, status_history: [{ timestamp: p.allocated_at || p.created_at }],
    delivery_instructions: p.note || "",
  }));

  const paintMove = async (id, lane) => {
    const label = { paid: "Paid", canberra: "Canberra Run", jindabyne: "Jindabyne Run", other: "Other Run" }[lane];
    const prev = paint;
    setPaint((cur) => cur.map((p) => (p.id === id ? { ...p, delivery_run: lane === "paid" ? null : lane, driver_id: lane === "paid" ? null : (p.driver_id || "pending") } : p)));
    try {
      const { data } = await api.post(`/paint/admin/orders/${id}/board-move`, { lane, run_date: runDate });
      setPaint((cur) => cur.map((p) => (p.id === id ? data : p)));
      toast.success(`Paint for ${data.customer?.name} → ${label}${data.driver_name ? ` — ${data.driver_name} sees it on their jobs page` : ""}`);
    } catch (e) { setPaint(prev); toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't move that paint order"); }
  };

  const paintDelivered = async (bubble) => {
    const who = window.prompt(`Mark paint for ${bubble.paint.customer?.name} as delivered.\n\nReceived by (optional):`);
    if (who === null) return;
    try {
      const fd = new FormData(); fd.append("received_by", who || "");
      await api.post(`/paint/admin/orders/${bubble.id}/delivered`, fd);
      toast.success("Paint order delivered" + (bubble.paint.customer?.phone ? " — customer texted" : ""));
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const switchView = (v) => { setView(v); localStorage.setItem("kc-orders-view", v); };

  const boardMove = async (orderId, lane) => {
    if (paintBubbles.some((b) => b.id === orderId)) return paintMove(orderId, lane);
    const prev = orders;
    const label = { paid: "Paid", canberra: "Canberra Run", jindabyne: "Jindabyne Run", other: "Other Run" }[lane];
    setOrders((cur) => cur.map((o) => {
      if (o.id !== orderId) return o;
      if (lane === "paid") return { ...o, delivery_run: null, status: o.status === "pending_payment" ? "confirmed" : o.status === "assigned" ? (o.billing === "prepaid" ? "paid" : "confirmed") : o.status };
      return { ...o, delivery_run: lane, status: ["paid", "confirmed"].includes(o.status) ? "assigned" : o.status };
    }));
    try {
      const { data } = await api.post(`/admin/orders/${orderId}/board-move`, { lane, run_date: runDate });
      setOrders((cur) => cur.map((o) => (o.id === orderId ? data : o)));
      const runLabel = { canberra: "Canberra Run", jindabyne: "Jindabyne Run", other: "Other Run" }[data.delivery_run];
      toast.success(lane === "paid" && prev.find((o) => o.id === orderId)?.status === "pending_payment"
        ? `${data.tracking_number} allocated to account billing${runLabel ? ` & placed on ${runLabel} — ${data.driver_name}` : ""}`
        : `${data.tracking_number} moved to ${label}${data.driver_name ? ` — ${data.driver_name} notified on their jobs page` : ""}`);
    } catch (e) {
      setOrders(prev);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't move that order");
    }
  };

  const allocateConfirmed = async () => {
    try {
      const { data } = await api.post("/admin/orders/allocate-confirmed");
      toast.success(data.allocated.length
        ? `${data.allocated.length} account order${data.allocated.length === 1 ? "" : "s"} placed on runs and drivers' pages`
        : "No confirmed account orders waiting");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const resequence = async (lane, ids) => {
    setOrders((cur) => cur.map((o) => (ids.includes(o.id) ? { ...o, run_sequence: ids.indexOf(o.id) } : o)));
    setPaint((cur) => cur.map((p) => (ids.includes(p.id) ? { ...p, run_sequence: ids.indexOf(p.id) } : p)));
    try {
      await api.post("/admin/orders/run-sequence", { lane, order_ids: ids });
      toast.success("Stop order saved — the driver sees the new sequence");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't save stop order"); load(); }
  };

  const assign = async (orderId, driverId) => {
    try { await api.post(`/admin/orders/${orderId}/assign`, { driver_id: driverId }); toast.success("Driver assigned"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const closeOnforward = async (o) => {
    const receivedBy = window.prompt(`Confirm ${o.tracking_number} was delivered by ${o.onforward?.carrier || "the partner carrier"}.\n\nReceived by (optional):`);
    if (receivedBy === null) return;
    try {
      await api.post(`/admin/orders/${o.id}/close-onforward`, { received_by: receivedBy || "" });
      toast.success("Closed out — marked delivered");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const resendInvoice = async (o) => {
    try {
      await api.post(`/orders/${o.id}/resend-invoice`);
      toast.success(`Invoice for ${o.tracking_number} emailed to ${o.customer_name}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't send the invoice"); }
  };

  const q = search.trim().toLowerCase();
  const filtered = (orders || []).filter((o) =>
    (!q || o.tracking_number.toLowerCase().includes(q) || (o.customer_name || "").toLowerCase().includes(q)) &&
    (!dateFilter || (o.created_at || "").slice(0, 10) === dateFilter || o.scheduled_date === dateFilter || o.run_date === dateFilter));

  if (!orders) return <Loading />;
  return (
    <>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-lg border border-border bg-secondary/40 p-0.5">
          <button onClick={() => switchView("board")} data-testid="orders-view-board"
            className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${view === "board" ? "bg-kilted-green text-white" : "text-muted-foreground hover:text-foreground"}`}>
            <LayoutGrid className="mr-1 inline h-3.5 w-3.5" />Board
          </button>
          <button onClick={() => switchView("table")} data-testid="orders-view-table"
            className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${view === "table" ? "bg-kilted-green text-white" : "text-muted-foreground hover:text-foreground"}`}>
            <TableIcon className="mr-1 inline h-3.5 w-3.5" />Table
          </button>
        </div>
        {view === "board" && (
          <div className="flex flex-wrap items-center gap-3">
            <Button size="sm" variant="outline" onClick={allocateConfirmed} data-testid="allocate-confirmed-button"
              className="h-9 gap-1.5 border-kilted-green text-xs text-kilted-green hover:bg-kilted-green/10">
              <Truck className="h-3.5 w-3.5" /> Allocate account orders
            </Button>
            <div className="w-44"><DatePickerAU value={runDate} onChange={(v) => setRunDate(v || new Date().toLocaleDateString("sv-SE"))} testid="board-run-date" /></div>
            <p className="hidden text-xs text-muted-foreground lg:block">Drag between lanes · drag up/down in a run to set stop order · click to open</p>
          </div>
        )}
      </div>
      <Dialog open={!!paintView} onOpenChange={(v) => { if (!v) setPaintView(null); }}>
        <DialogContent className="max-w-md" data-testid="paint-bubble-dialog">
          {paintView && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">Paint order — {paintView.customer?.name}</DialogTitle>
                <DialogDescription>From {paintView.shop_name} · ordered {paintView.created_at.slice(0, 10)}</DialogDescription>
              </DialogHeader>
              <div className="space-y-2 text-sm">
                <p><span className="text-muted-foreground">Deliver to:</span> {paintView.customer?.address || "—"}{paintView.customer?.phone ? ` · ${paintView.customer.phone}` : ""}</p>
                <p><span className="text-muted-foreground">Items:</span> {(paintView.lines || []).map((l) => `${l.qty}× ${l.label}`).join(", ")} · <b>${(paintView.total || 0).toFixed(2)}</b> on account</p>
                {paintView.note && <p className="rounded bg-kilted-gold/15 px-2 py-1 text-xs font-medium text-kilted-green">Note: {paintView.note}</p>}
                <div className="flex items-center gap-2"><PaintDeliveryStatus order={paintView} />{paintView.driver_name && <span className="text-xs text-muted-foreground">Driver: {paintView.driver_name}</span>}</div>
                <p className="text-[11px] text-muted-foreground">Edit quantities or customer details in the Paint tab.</p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      <CancelOrderDialog order={cancelTarget} user={user} trigger={false} open={!!cancelTarget} onOpenChange={(v) => { if (!v) setCancelTarget(null); }} onCancelled={load} />
      {view === "board" ? (
        <OrdersBoard orders={[...orders, ...paintBubbles]} onMove={boardMove} runDate={runDate} onResequence={resequence}
          onOpen={(o) => (o.kind === "paint" ? setPaintView(o.paint) : navigate(`/orders/${o.id}`))}
          onCancel={setCancelTarget}
          onQuick={(o, status, label) => (o.kind === "paint" ? paintDelivered(o) : quickStatus(o, status, label))} />
      ) : (
    <>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <div className="relative w-64">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search con note # or customer…"
            className="h-9 pl-8" data-testid="orders-search-input" />
        </div>
        <div className="w-44"><DatePickerAU value={dateFilter} onChange={setDateFilter} placeholder="Filter by date" testid="orders-date-filter" /></div>
        {(q || dateFilter) && <p className="text-xs text-muted-foreground" data-testid="orders-filter-count">{filtered.length} of {orders.length} orders</p>}
      </div>
    <Card className="mt-4">
      <CardContent className="p-0">
        <Table data-testid="admin-orders-table">
          <TableHeader>
            <TableRow><TableHead>Tracking</TableHead><TableHead>Customer</TableHead><TableHead>Route</TableHead><TableHead>Status</TableHead><TableHead>Total</TableHead><TableHead>Driver</TableHead><TableHead>Invoice</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((o) => (
              <TableRow key={o.id} data-testid={`admin-order-row-${o.tracking_number}`} className="cursor-pointer" onClick={() => navigate(`/orders/${o.id}`)}>
                <TableCell className="font-semibold text-kilted-green">{o.tracking_number}</TableCell>
                <TableCell>{o.customer_name}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{o.pickup.city} → {o.dropoff.city}</TableCell>
                <TableCell><StatusBadge status={o.status} /></TableCell>
                <TableCell className="font-medium">${o.price.total.toFixed(2)}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  {o.status === "onforwarded" ? (
                    <Button size="sm" variant="outline" data-testid={`close-onforward-${o.tracking_number}`}
                      className="h-8 gap-1 border-emerald-500 text-xs text-emerald-700 hover:bg-emerald-50"
                      onClick={() => closeOnforward(o)}>
                      Confirm delivered
                    </Button>
                  ) : o.driver_name ? <span className="text-sm">{o.driver_name}</span> : ["paid", "confirmed"].includes(o.status) ? (
                    <Select onValueChange={(v) => assign(o.id, v)}>
                      <SelectTrigger className="h-8 w-36" data-testid={`assign-select-${o.tracking_number}`}><SelectValue placeholder="Assign…" /></SelectTrigger>
                      <SelectContent>{drivers.map((d) => <SelectItem key={d.id} value={d.id} data-testid={`assign-driver-${d.id}`}>{d.name} ({d.active_jobs})</SelectItem>)}</SelectContent>
                    </Select>
                  ) : <span className="text-xs text-muted-foreground">—</span>}
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  {o.payment?.status === "paid" ? (
                    <Button size="sm" variant="ghost" onClick={() => resendInvoice(o)} data-testid={`resend-invoice-${o.tracking_number}`}
                      title="Email the PDF invoice again" className="h-8 gap-1 text-xs text-kilted-green hover:bg-kilted-green/10">
                      <Mail className="h-3.5 w-3.5" /> Invoice
                    </Button>
                  ) : <span className="text-xs text-muted-foreground">—</span>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
    </>
      )}
    </>
  );
}

function DriversTab() {
  const [drivers, setDrivers] = useState(null);
  const [runDrivers, setRunDrivers] = useState(null);
  const load = useCallback(() => api.get("/admin/drivers").then((r) => setDrivers(r.data)), []);
  useEffect(() => { load(); api.get("/admin/run-drivers").then((r) => setRunDrivers(r.data)).catch(() => {}); }, [load]);

  const setRunOwner = async (run, driverId) => {
    const next = { ...runDrivers, [run]: driverId === "auto" ? "" : driverId };
    setRunDrivers(next);
    try {
      await api.put("/admin/run-drivers", next);
      toast.success(driverId === "auto" ? "Run set to least-busy driver" : "Run owner saved — new account orders go to them");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const setAutoAllocate = async (on) => {
    const next = { ...runDrivers, auto_allocate: on };
    setRunDrivers(next);
    try {
      await api.put("/admin/run-drivers", next);
      toast.success(on ? "Auto-allocation ON — account orders go straight onto runs" : "Auto-allocation OFF — account orders wait in Paid for you to drag onto a run");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!drivers) return <Loading />;
  return (
    <div className="mt-4 space-y-4">
      <div className="flex justify-end"><AddDriverDialog onAdded={load} /></div>
      {runDrivers && (
        <Card data-testid="run-owners-card">
          <CardHeader>
            <CardTitle className="text-lg">Run ownership</CardTitle>
            <p className="text-sm text-muted-foreground">Who gets new orders placed on each run — leave on "Least busy" to spread the load automatically.</p>
            <label className="mt-2 flex cursor-pointer items-center gap-3 rounded-md border border-kilted-gold/50 bg-kilted-gold/10 px-3 py-2" data-testid="auto-allocate-row">
              <Switch checked={!!runDrivers.auto_allocate} onCheckedChange={setAutoAllocate} data-testid="auto-allocate-switch" />
              <span className="text-sm"><b>Auto-allocate account orders to runs</b> — {runDrivers.auto_allocate ? "on: new account bookings land on a run and driver instantly" : "off: new account bookings wait in the Paid lane until you drag them onto a run"}</span>
            </label>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-3">
            {[["canberra", "Canberra Run"], ["jindabyne", "Jindabyne Run"], ["other", "Other Run"]].map(([run, label]) => (
              <div key={run} className="space-y-1.5">
                <Label>{label}</Label>
                <Select value={runDrivers[run] || "auto"} onValueChange={(v) => setRunOwner(run, v)}>
                  <SelectTrigger data-testid={`run-owner-${run}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Least busy (automatic)</SelectItem>
                    {drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
      <Card>
        <CardContent className="p-0">
          <Table data-testid="admin-drivers-table">
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Vehicle</TableHead><TableHead>Active jobs</TableHead><TableHead className="w-24"></TableHead></TableRow></TableHeader>
            <TableBody>
              {drivers.map((d) => (
                <TableRow key={d.id} data-testid={`driver-row-${d.id}`}>
                  <TableCell className="font-semibold">{d.name}{d.also_driver && <span className="ml-2 rounded-full bg-kilted-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase text-kilted-green">Admin</span>}</TableCell>
                  <TableCell className="text-muted-foreground">{d.email}</TableCell>
                  <TableCell>{d.vehicle || "—"}</TableCell>
                  <TableCell><span className="rounded-full bg-secondary px-2 py-0.5 text-sm font-medium">{d.active_jobs}</span></TableCell>
                  <TableCell>
                    <Button size="sm" variant="outline" data-testid={`remove-driver-${d.id}`}
                      onClick={async () => {
                        if (!window.confirm(`Remove ${d.name} from the driver roster?`)) return;
                        try { await api.delete(`/admin/drivers/${d.id}`); toast.success(`${d.name} removed from drivers`); load(); }
                        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
                      }}
                      className="h-7 gap-1 px-2 text-xs text-red-600 hover:bg-red-50">
                      <Trash2 className="h-3 w-3" /> Remove
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function AddDriverDialog({ onAdded }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", vehicle: "", password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async () => {
    setBusy(true);
    try { await api.post("/admin/drivers", form); toast.success("Driver added"); setOpen(false); setForm({ name: "", email: "", phone: "", vehicle: "", password: "" }); onAdded(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="add-driver-button" className="gap-2 bg-kilted-green hover:bg-kilted-greenLight"><Plus className="h-4 w-4" /> Add driver</Button></DialogTrigger>
      <DialogContent data-testid="add-driver-dialog">
        <DialogHeader><DialogTitle>Add a driver</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={set("name")} data-testid="driver-name-input" /></div>
          <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={set("email")} data-testid="driver-email-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} data-testid="driver-phone-input" /></div>
            <div className="space-y-1.5"><Label>Vehicle</Label><Input value={form.vehicle} onChange={set("vehicle")} data-testid="driver-vehicle-input" /></div>
          </div>
          <div className="space-y-1.5"><Label>Temp password</Label><Input type="text" value={form.password} onChange={set("password")} data-testid="driver-password-input" /></div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={busy} data-testid="driver-save-button" className="gap-2 bg-kilted-green hover:bg-kilted-greenLight">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CustomersTab() {
  const { user } = useAuth();
  const [customers, setCustomers] = useState(null);
  const load = useCallback(() => api.get("/admin/customers").then((r) => setCustomers(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const changeRole = async (id, role) => {
    try { await api.patch(`/admin/users/${id}/role`, { role }); toast.success(`Role updated to ${role}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const changeType = async (id, customer_type) => {
    try {
      const { data } = await api.patch(`/admin/users/${id}/customer-type`, { customer_type });
      if (customer_type === "partner") {
        toast.success(data.welcome_sent ? "Type updated — partner welcome kit emailed" : "Type updated (welcome email couldn't be sent)");
      } else toast.success("Type updated");
      load();
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggleBan = async (c) => {
    const banning = c.status !== "banned";
    if (banning && !window.confirm(`Ban ${c.name || c.email}? They won't be able to log in or book until unbanned.`)) return;
    try {
      await api.patch(`/admin/users/${c.id}/status`, { status: banning ? "banned" : "active" });
      toast.success(banning ? "Account banned — logins blocked" : "Account re-enabled");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const resendVerify = async (c) => {
    try {
      const { data } = await api.post(`/admin/users/${c.id}/resend-verification`);
      toast.success(`Verification email sent to ${data.sent_to}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const markVerified = async (c) => {
    if (!window.confirm(`Mark ${c.name || c.email} as verified without them clicking the email link?`)) return;
    try {
      await api.post(`/admin/users/${c.id}/verify-email`);
      toast.success("Marked as verified — they can book now");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  if (!customers) return <Loading />;
  return (
    <Card className="mt-4">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0 pb-3">
        <div>
          <CardTitle className="text-lg">Customers & accounts</CardTitle>
          <p className="text-sm text-muted-foreground">Open "Details" to see everything captured at sign-up and edit name, email, mobile and your private note.</p>
        </div>
        <Button size="sm" variant="outline" data-testid="download-signup-spec" className="gap-1.5"
          onClick={() => downloadText("/admin/users/signup-fields.txt", "kilted-courier-signup-fields.txt").catch(() => toast.error("Download failed"))}>
          <Download className="h-4 w-4" /> Sign-up fields (.txt) for the HLD
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        <Table data-testid="admin-customers-table">
          <TableHeader><TableRow><TableHead className="w-12">#</TableHead><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Joined</TableHead><TableHead>Orders</TableHead><TableHead>Shares</TableHead><TableHead>Type</TableHead><TableHead>Role</TableHead><TableHead>Access</TableHead></TableRow></TableHeader>
          <TableBody>
            {customers.map((c, i) => (
              <React.Fragment key={`user-${c.id}`}>
                {(i === 0 || customers[i - 1].group !== c.group) && (
                  <TableRow key={`header-${c.group}`} className="bg-secondary/60 hover:bg-secondary/60">
                    <TableCell colSpan={9} className="py-1.5 text-xs font-bold uppercase tracking-wide text-kilted-green" data-testid={`group-header-${c.group.replace(/\s/g, "-").toLowerCase()}`}>
                      {c.group}
                    </TableCell>
                  </TableRow>
                )}
              <TableRow data-testid={`customer-row-${c.id}`} className={c.status === "banned" ? "opacity-60" : ""}>
                <TableCell className="text-sm font-bold text-kilted-green" data-testid={`customer-no-${c.id}`}>{c.customer_no ? `#${c.customer_no}` : "—"}</TableCell>
                <TableCell className="font-semibold">
                  {c.name}
                  {c.status === "banned" && <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold uppercase text-red-700">Banned</span>}
                  {c.email_verified === false && <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-700">Unverified</span>}
                  {c.frequent_canceller && <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold uppercase text-red-700" title={`${c.cancels_30d} cancellations in the last 30 days — worth a call before their next booking`} data-testid={`frequent-canceller-${c.id}`}>{c.cancels_30d} cancels / 30d</span>}
                </TableCell>
                <TableCell className="text-muted-foreground">{c.email}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{c.joined}</TableCell>
                <TableCell>{c.order_count}</TableCell>
                <TableCell data-testid={`shares-${c.id}`}>
                  {c.shares_sent > 0 ? (
                    <span className="text-xs">
                      <b>{c.shares_sent}</b> sent · <b className={c.shares_booked > 0 ? "text-emerald-700" : ""}>{c.shares_booked}</b> booked
                    </span>
                  ) : <span className="text-xs text-muted-foreground">—</span>}
                </TableCell>
                <TableCell>
                  {c.role === "admin" ? <span className="text-xs text-muted-foreground">—</span> : (
                  <Select value={c.customer_type || "normal"} onValueChange={(v) => changeType(c.id, v)}>
                    <SelectTrigger className="h-8 w-36" data-testid={`type-select-${c.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="normal" data-testid={`type-normal-${c.id}`}>Normal (prepay)</SelectItem>
                      <SelectItem value="on_account" data-testid={`type-account-${c.id}`}>On Account</SelectItem>
                      <SelectItem value="florist" data-testid={`type-florist-${c.id}`}>Florist</SelectItem>
                      <SelectItem value="partner" data-testid={`type-partner-${c.id}`}>Partner (quote & SMS only)</SelectItem>
                      <SelectItem value="paint_shop" data-testid={`type-paint-${c.id}`}>Paint shop</SelectItem>
                    </SelectContent>
                  </Select>
                  )}
                </TableCell>
                <TableCell>
                  {c.role === "admin" ? <span className="text-xs font-semibold uppercase text-kilted-green">Admin</span> : (
                  <Select value={c.role} onValueChange={(v) => changeRole(c.id, v)}>
                    <SelectTrigger className="h-8 w-32" data-testid={`role-select-${c.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="customer" data-testid={`role-customer-${c.id}`}>Customer</SelectItem>
                      <SelectItem value="driver" data-testid={`role-driver-${c.id}`}>Driver</SelectItem>
                      <SelectItem value="admin" data-testid={`role-admin-${c.id}`}>Admin</SelectItem>
                    </SelectContent>
                  </Select>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap items-center gap-1.5">
                  {(c.role !== "admin" || c.id === user?.id) && <CustomerDetailsDialog customer={c} onSaved={load} />}
                  {c.role !== "admin" && (
                  <Button size="sm" variant="outline" onClick={() => toggleBan(c)} data-testid={`ban-toggle-${c.id}`}
                    className={c.status === "banned"
                      ? "h-8 border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                      : "h-8 border-red-300 text-red-600 hover:bg-red-50"}>
                    {c.status === "banned" ? "Unban" : "Ban"}
                  </Button>
                  )}
                  {c.email_verified === false && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => resendVerify(c)} data-testid={`resend-verify-${c.id}`}
                        className="h-8 border-amber-300 text-amber-700 hover:bg-amber-50">
                        Resend verify email
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => markVerified(c)} data-testid={`mark-verified-${c.id}`}
                        className="h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                        Mark verified
                      </Button>
                    </>
                  )}
                  </div>
                </TableCell>
              </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function PricingTab() {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fuel, setFuel] = useState(null);
  const [fuelBusy, setFuelBusy] = useState(false);
  const [priceRequests, setPriceRequests] = useState([]);
  const [reqPrices, setReqPrices] = useState({});
  useEffect(() => {
    api.get("/admin/pricing").then((r) => setP(r.data));
    api.get("/fuel-levy").then((r) => setFuel(r.data)).catch(() => {});
    api.get("/admin/florist-price-requests").then((r) => setPriceRequests(r.data)).catch(() => {});
  }, []);
  const approveRequest = async (req) => {
    const price = parseFloat(reqPrices[req.id]);
    if (!price || price <= 0) { toast.error("Enter a price first"); return; }
    try {
      await api.post(`/admin/florist-price-requests/${req.id}/approve`, { price });
      toast.success(`${req.city_display} added at $${price.toFixed(2)}`);
      setPriceRequests((prev) => prev.filter((r) => r.town !== req.town));
      const { data } = await api.get("/admin/pricing");
      setP((prev) => (prev ? { ...prev, florist_town_prices: data.florist_town_prices, florist_town_coords: data.florist_town_coords } : data));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const dismissRequest = async (req) => {
    try {
      await api.post(`/admin/florist-price-requests/${req.id}/decline`);
      setPriceRequests((prev) => prev.filter((r) => r.id !== req.id));
      toast.success("Request dismissed");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const refreshFuel = async () => {
    setFuelBusy(true);
    try { const { data } = await api.post("/admin/fuel-levy/refresh"); setFuel(data); toast.success("Fuel levy refreshed from FuelCheck NSW"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setFuelBusy(false); }
  };
  if (!p) return <Loading />;
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/pricing", {
        base_fare: parseFloat(p.base_fare), per_km: parseFloat(p.per_km), per_km_account: parseFloat(p.per_km_account ?? p.per_km),
        winback_credit: parseFloat(p.winback_credit ?? 10), partner_commission_pct: parseFloat(p.partner_commission_pct ?? 10),
        paint_prices: Object.fromEntries(Object.entries(p.paint_prices || {}).map(([k, v]) => [k, parseFloat(v) || 0])),
        florist_fixed_price: parseFloat(p.florist_fixed_price ?? 25),
        florist_town_prices: Object.fromEntries(Object.entries(p.florist_town_prices || {}).map(([k, v]) => [k, parseFloat(v) || 0])),
        gst_rate: parseFloat(p.gst_rate ?? 0.1),
        size_surcharge: Object.fromEntries(Object.entries(p.size_surcharge).map(([k, v]) => [k, parseFloat(v)])),
        extra_item_km_pct: Object.fromEntries(Object.entries(p.extra_item_km_pct || { small: 10, medium: 10, large: 10 }).map(([k, v]) => [k, parseFloat(v) || 0])),
        service_multiplier: Object.fromEntries(Object.entries(p.service_multiplier).map(([k, v]) => [k, parseFloat(v)])),
        currency: p.currency,
      });
      toast.success("Pricing updated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-4 max-w-2xl space-y-4">
      <Card data-testid="fuel-levy-card" className="border-kilted-gold/40 bg-kilted-gold/5">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-lg">Fuel Levy</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">Auto-updates weekly (Mon 00:00 AEST) from FuelCheck NSW · {fuel?.station || "Metro West Cooma"}</p>
          </div>
          <Button onClick={refreshFuel} disabled={fuelBusy} variant="outline" size="sm" data-testid="fuel-refresh-button" className="gap-1.5">
            {fuelBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh
          </Button>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-8">
            <div>
              <div className="text-xs font-medium uppercase text-muted-foreground">Fuel Levy (% of fare)</div>
              <div className="font-heading text-3xl font-extrabold text-kilted-green" data-testid="fuel-levy-value">{fuel?.fuel_levy != null ? fuel.fuel_levy.toFixed(2) : "—"}</div>
            </div>
            <div>
              <div className="text-xs font-medium uppercase text-muted-foreground">Current U91 price</div>
              <div className="text-lg font-semibold" data-testid="fuel-current-price">{fuel?.current_price != null ? `${fuel.current_price.toFixed(1)} c/L` : "—"}</div>
            </div>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">FL % = (current price − 100) × 10% · applied as a % of the fare subtotal · last updated {fuel?.updated_at ? new Date(fuel.updated_at).toLocaleString("en-AU") : "—"}</p>
        </CardContent>
      </Card>

      {priceRequests.length > 0 && (
        <Card data-testid="price-requests-card" className="border-pink-200 bg-pink-50/40">
          <CardHeader>
            <CardTitle className="text-lg">Florist price requests</CardTitle>
            <p className="text-sm text-muted-foreground">A florist asked for a fixed rate on a town outside the list — set the price and it's added instantly.</p>
          </CardHeader>
          <CardContent className="space-y-3">
            {priceRequests.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-white p-3" data-testid={`price-request-row-${r.town.replace(/\s+/g, "-")}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold capitalize text-kilted-green">{r.city_display}</p>
                  <p className="text-xs text-muted-foreground">Requested by {r.florist_name || r.florist_email} · {new Date(r.created_at).toLocaleDateString("en-AU")}</p>
                </div>
                <Input type="number" step="0.5" min="1" placeholder="$ inc GST" value={reqPrices[r.id] ?? ""}
                  onChange={(e) => setReqPrices((prev) => ({ ...prev, [r.id]: e.target.value }))}
                  className="w-28" data-testid={`price-request-amount-${r.town.replace(/\s+/g, "-")}`} />
                <Button size="sm" onClick={() => approveRequest(r)} data-testid={`price-request-approve-${r.town.replace(/\s+/g, "-")}`} className="gap-1.5 bg-kilted-green hover:bg-kilted-greenLight">
                  <Plus className="h-3.5 w-3.5" /> Add to list
                </Button>
                <Button size="sm" variant="ghost" onClick={() => dismissRequest(r)} data-testid={`price-request-dismiss-${r.town.replace(/\s+/g, "-")}`} className="text-muted-foreground">Dismiss</Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card data-testid="pricing-form">
        <CardHeader><CardTitle className="text-lg">Pricing configuration</CardTitle></CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-1.5"><Label>Base fare ($)</Label><Input type="number" step="0.01" value={p.base_fare} onChange={(e) => setP({ ...p, base_fare: e.target.value })} data-testid="pricing-base-fare" /></div>
          <div className="space-y-1.5"><Label>Non-account rate per km ($)</Label><Input type="number" step="0.01" value={p.per_km} onChange={(e) => setP({ ...p, per_km: e.target.value })} data-testid="pricing-per-km" /></div>
          <div className="space-y-1.5"><Label>Account rate per km ($)</Label><Input type="number" step="0.01" value={p.per_km_account ?? ""} onChange={(e) => setP({ ...p, per_km_account: e.target.value })} data-testid="pricing-per-km-account" /></div>
          <div className="space-y-1.5"><Label>Florist other areas ($)</Label><Input type="number" step="0.01" value={p.florist_fixed_price ?? 25} onChange={(e) => setP({ ...p, florist_fixed_price: e.target.value })} data-testid="pricing-florist" /></div>
          <div className="space-y-1.5"><Label>GST (%)</Label><Input type="number" step="0.5" value={Number(p.gst_rate ?? 0.1) * 100} onChange={(e) => setP({ ...p, gst_rate: (parseFloat(e.target.value) || 0) / 100 })} data-testid="pricing-gst" /></div>
          <div className="space-y-1.5"><Label>Win-back credit ($)</Label><Input type="number" step="0.5" value={p.winback_credit ?? 10} onChange={(e) => setP({ ...p, winback_credit: e.target.value })} data-testid="pricing-winback-credit" /></div>
          <div className="space-y-1.5"><Label>Partner commission (%)</Label><Input type="number" step="0.5" value={p.partner_commission_pct ?? 10} onChange={(e) => setP({ ...p, partner_commission_pct: e.target.value })} data-testid="pricing-partner-commission" /></div>
          {[["carton", "Paint: Carton ($)"], ["tin_4l", "Paint: Tin 4L ($)"], ["tin_10l", "Paint: Tin 10L ($)"], ["tin_15l", "Paint: Tin 15L ($)"], ["tin_20l", "Paint: Tin 20L ($)"]].map(([k, l]) => (
            <div key={k} className="space-y-1.5"><Label>{l}</Label><Input type="number" step="0.5" value={p.paint_prices?.[k] ?? ""} onChange={(e) => setP({ ...p, paint_prices: { ...p.paint_prices, [k]: e.target.value } })} data-testid={`pricing-paint-${k}`} /></div>
          ))}
        </div>
        <div>
          <Label className="mb-2 block">Size surcharge ($)</Label>
          <div className="grid grid-cols-4 gap-3">
            {Object.entries(p.size_surcharge).map(([k, v]) => (
              <div key={k} className="space-y-1"><Label className="text-xs capitalize">{k}</Label><Input type="number" step="0.01" value={v} onChange={(e) => setP({ ...p, size_surcharge: { ...p.size_surcharge, [k]: e.target.value } })} data-testid={`pricing-size-${k}`} /></div>
            ))}
          </div>
        </div>
        <div>
          <Label className="mb-2 block">Additional item rate (% of km cost per extra package)</Label>
          <div className="grid grid-cols-4 gap-3">
            {["small", "medium", "large"].map((k) => (
              <div key={k} className="space-y-1"><Label className="text-xs capitalize">{k}</Label><Input type="number" step="0.5" value={(p.extra_item_km_pct || {})[k] ?? 10} onChange={(e) => setP({ ...p, extra_item_km_pct: { ...(p.extra_item_km_pct || {}), [k]: e.target.value } })} data-testid={`pricing-extra-item-${k}`} /></div>
            ))}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">The largest package travels at the normal km rate — each extra package adds its size's % of the km cost.</p>
        </div>
        <div>
          <Label className="mb-2 block">Florist town prices ($ inc GST, by drop-off town)</Label>
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
            {Object.entries(p.florist_town_prices || {}).map(([k, v]) => (
              <div key={k} className="space-y-1"><Label className="text-xs capitalize">{k}</Label><Input type="number" step="0.5" value={v} onChange={(e) => setP({ ...p, florist_town_prices: { ...p.florist_town_prices, [k]: e.target.value } })} data-testid={`pricing-town-${k.replace(/\s+/g, "-")}`} /></div>
            ))}
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">Drop-off towns not listed use the "Florist other areas" rate above.</p>
        </div>
        <div>
          <Label className="mb-2 block">Service multiplier</Label>
          <div className="grid grid-cols-3 gap-3">
            {Object.entries(p.service_multiplier).map(([k, v]) => (
              <div key={k} className="space-y-1"><Label className="text-xs">{serviceLabel(k)}</Label><Input type="number" step="0.1" value={v} onChange={(e) => setP({ ...p, service_multiplier: { ...p.service_multiplier, [k]: e.target.value } })} data-testid={`pricing-service-${k}`} /></div>
            ))}
          </div>
        </div>
        <Button onClick={save} disabled={busy} data-testid="pricing-save-button" className="gap-2 bg-kilted-green hover:bg-kilted-greenLight">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save pricing</Button>
      </CardContent>
      </Card>
      <BranchTransferCard />
    </div>
  );
}

function AccountsTab() {
  const [data, setData] = useState(null);
  const [xero, setXero] = useState(null);
  const [xeroInvoices, setXeroInvoices] = useState([]);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState([]);
  const [manifestFor, setManifestFor] = useState(null);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));

  const load = useCallback(() => {
    api.get("/admin/eom").then((r) => setData(r.data)).catch(() => {});
    api.get("/xero/status").then((r) => setXero(r.data)).catch(() => setXero({ connected: false }));
    api.get("/admin/xero/invoices").then((r) => setXeroInvoices(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    const p = new URLSearchParams(window.location.search);
    if (p.get("xero") === "connected") toast.success("Xero connected successfully");
    if (p.get("xero") === "error") toast.error("Xero connection failed — please try again");
    if (p.get("xero")) window.history.replaceState({}, "", "/admin");
  }, [load]);

  const connect = async () => {
    try {
      const { data } = await api.post("/xero/connect-init");
      window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/xero/connect?otp=${encodeURIComponent(data.otp)}`;
    } catch { toast.error("Could not start the Xero connection — please try again"); }
  };
  const disconnect = async () => {
    await api.post("/xero/disconnect"); toast.success("Xero disconnected"); load();
  };
  const push = async (ids) => {
    setBusy(ids.length === 1 ? ids[0] : true);
    try {
      const { data: res } = await api.post("/admin/xero/invoice-month", { month, customer_ids: ids });
      if (res.invoiced_customers > 0) toast.success(`Created ${res.invoiced_customers} Xero invoice(s) covering ${res.invoiced_orders} deliver${res.invoiced_orders === 1 ? "y" : "ies"}`);
      else toast.info("No on-account deliveries to invoice");
      if (res.errors?.length) toast.error(`${res.errors.length} customer(s) failed — check details`);
      setSelected([]);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  if (!data || !xero) return <Loading />;
  const connected = xero.connected;
  return (
    <div className="mt-4 space-y-4">
      <Card data-testid="xero-connection-card">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-lg">Xero connection</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              {connected ? <>Connected to <span className="font-semibold text-kilted-green">{xero.tenant_name}</span></> : "Connect your Xero organisation to push on-account invoices."}
            </p>
          </div>
          {connected
            ? <Button variant="outline" onClick={disconnect} data-testid="xero-disconnect-button">Disconnect</Button>
            : <Button onClick={connect} data-testid="xero-connect-button" className="bg-kilted-green hover:bg-kilted-greenLight">Connect Xero</Button>}
        </CardHeader>
      </Card>

      <Card data-testid="accounts-tab">
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between space-y-0">
          <div>
            <CardTitle className="text-lg">On-account billing (monthly Xero invoicing)</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">Unbilled on-account deliveries, collated per customer. One consolidated invoice per customer.</p>
          </div>
          <div className="flex items-center gap-2">
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="eom-month-input" className="w-40" title="Bills unbilled jobs up to the end of this month" />
            <Button onClick={() => push(selected)} disabled={!!busy || selected.length === 0 || !connected} data-testid="eom-push-button" className="gap-2 bg-kilted-green hover:bg-kilted-greenLight">
              {busy === true ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Push selected ({selected.length})
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!connected && <p className="mb-3 text-sm text-kilted-gold">Connect Xero above to enable invoicing.</p>}
          {data.count === 0 ? <p className="text-sm text-muted-foreground">No unbilled on-account deliveries.</p> : (
            <>
              <Table>
                <TableHeader><TableRow>
                  <TableHead className="w-10">
                    <Checkbox checked={selected.length === data.customers.length} onCheckedChange={(v) => setSelected(v ? data.customers.map((c) => c.customer_id) : [])} data-testid="eom-select-all" aria-label="Select all accounts" />
                  </TableHead>
                  <TableHead>Customer</TableHead><TableHead>Deliveries</TableHead><TableHead>Total (inc GST)</TableHead><TableHead className="text-right">Xero</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {data.customers.map((c) => (
                    <TableRow key={c.customer_id} data-testid={`eom-row-${c.customer_id}`}>
                      <TableCell>
                        <Checkbox checked={selected.includes(c.customer_id)} data-testid={`eom-select-${c.customer_id}`}
                          onCheckedChange={(v) => setSelected((s) => (v ? [...s, c.customer_id] : s.filter((x) => x !== c.customer_id)))} />
                      </TableCell>
                      <TableCell className="font-semibold">
                        <button onClick={() => setManifestFor(c)} data-testid={`eom-open-manifest-${c.customer_id}`} className="text-left text-kilted-green underline-offset-2 hover:underline">{c.customer_name}</button>
                      </TableCell>
                      <TableCell>{c.orders}{c.paint_orders ? <span className="text-muted-foreground"> + {c.paint_orders} paint</span> : null}</TableCell>
                      <TableCell>${c.total.toFixed(2)}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" onClick={() => push([c.customer_id])} disabled={!!busy || !connected} data-testid={`eom-push-one-${c.customer_id}`} className="gap-1.5">
                          {busy === c.customer_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Push
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="mt-4 flex justify-end text-sm font-bold" data-testid="eom-grand-total">Grand total: ${data.grand_total.toFixed(2)}</div>
              <p className="mt-2 text-xs text-muted-foreground">Auto-runs on the 1st of each month for the previous month (all accounts). Tick accounts and use "Push selected", or "Push" on one row, to bill mid-month.</p>
            </>
          )}
        </CardContent>
      </Card>

      <StripePayoutsCard connected={connected} onPushed={load} />
      {manifestFor && <AccountManifestDialog customer={manifestFor} initialMonth="unbilled" onClose={() => setManifestFor(null)} onChanged={load} />}

      <XeroContactsCard connected={connected} />

      <Card data-testid="xero-invoices-card">
        <CardHeader><CardTitle className="text-lg">Recent Xero invoices</CardTitle></CardHeader>
        <CardContent>
          {xeroInvoices.length === 0 ? (
            <p className="text-sm text-muted-foreground">No invoices pushed to Xero yet.</p>
          ) : (
            <Table>
              <TableHeader><TableRow><TableHead>Invoice #</TableHead><TableHead>Customer</TableHead><TableHead>Deliveries</TableHead><TableHead>Total</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {xeroInvoices.map((inv) => (
                  <TableRow key={inv.invoice_id} data-testid={`xero-invoice-${inv.invoice_number}`}>
                    <TableCell className="font-medium">{inv.invoice_number}</TableCell>
                    <TableCell>{inv.customer_name}</TableCell>
                    <TableCell>{inv.orders}</TableCell>
                    <TableCell>${Number(inv.total || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right">
                      <a href={inv.url} target="_blank" rel="noreferrer" data-testid={`xero-invoice-link-${inv.invoice_number}`} className="inline-flex items-center gap-1 text-sm font-medium text-kilted-green underline underline-offset-2 hover:text-kilted-greenLight">
                        Open in Xero <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ReportsTab() {
  const [r, setR] = useState(null);
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 864e5).toISOString().slice(0, 10);
  const [start, setStart] = useState(monthAgo);
  const [end, setEnd] = useState(today);
  const [dr, setDr] = useState(null);
  const [busy, setBusy] = useState(false);

  const runReport = useCallback(() => { setDr(null); api.get("/admin/finance/report", { params: { start, end } }).then((res) => setDr(res.data)).catch(() => toast.error("Report failed")); }, [start, end]);
  useEffect(() => {
    api.get("/admin/reports").then((res) => setR(res.data));
    runReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportCsv = async () => {
    setBusy(true);
    try {
      const res = await api.get("/admin/finance/report/export", { params: { start, end }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `report-${start}_to_${end}.csv`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); } finally { setBusy(false); }
  };

  return (
    <div className="mt-4 space-y-4" data-testid="admin-reports">
      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between space-y-0">
          <div><CardTitle className="text-lg">Date-range report</CardTitle><p className="mt-1 text-sm text-muted-foreground">Revenue &amp; volume between two dates.</p></div>
          <div className="flex flex-wrap items-center gap-2">
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="report-start" className="w-40" />
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="report-end" className="w-40" />
            <Button onClick={runReport} data-testid="report-run" className="bg-kilted-green hover:bg-kilted-greenLight">Run</Button>
            <Button variant="outline" onClick={exportCsv} disabled={busy || !dr} data-testid="report-export-csv" className="gap-1.5"><FileSpreadsheet className="h-4 w-4" /> CSV</Button>
          </div>
        </CardHeader>
        <CardContent>
          {!dr ? <Loading /> : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="report-metrics">
              <Metric label="Orders" value={dr.orders} plain />
              <Metric label="Revenue (inc GST)" value={dr.revenue} accent />
              <Metric label="GST" value={dr.gst} />
              <Metric label="Ex-GST" value={dr.ex_gst} />
            </div>
          )}
        </CardContent>
      </Card>
      {r && (
        <div className="grid gap-4 md:grid-cols-3">
          <ReportCard title="Orders by status" data={r.by_status} />
          <ReportCard title="Orders by service" data={r.by_service} />
          <ReportCard title="Revenue by city ($)" data={r.revenue_by_city} money />
        </div>
      )}
      <MonthlyRevenueChart />
    </div>
  );
}

function MonthlyRevenueChart() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/finance/monthly-revenue?months=12").then((res) => setData(res.data.months)).catch(() => setData([]));
  }, []);
  return (
    <Card data-testid="monthly-revenue-card">
      <CardHeader><CardTitle className="text-lg">Monthly revenue trend <span className="text-xs font-normal text-muted-foreground">(inc GST, last 12 months)</span></CardTitle></CardHeader>
      <CardContent>
        {!data ? <Loading /> : data.every((m) => m.revenue === 0) ? (
          <p className="text-sm text-muted-foreground">No revenue recorded yet.</p>
        ) : (
          <div className="h-64 w-full" data-testid="monthly-revenue-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${v}`} width={64} />
                <Tooltip formatter={(v) => [`$${Number(v).toFixed(2)}`, "Revenue"]} />
                <Line type="monotone" dataKey="revenue" stroke="#1B4332" strokeWidth={2.5} dot={{ r: 3, fill: "#1B4332" }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ReportCard({ title, data, money }) {
  const entries = Object.entries(data || {});
  const max = Math.max(1, ...entries.map(([, v]) => v));
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        {entries.length === 0 && <p className="text-sm text-muted-foreground">No data yet.</p>}
        {entries.map(([k, v]) => (
          <div key={k}>
            <div className="flex justify-between text-sm"><span className="capitalize text-muted-foreground">{k.replace("_", " ")}</span><span className="font-semibold">{money ? `$${v.toFixed(2)}` : v}</span></div>
            <div className="mt-1 h-2 rounded-full bg-secondary"><div className="h-2 rounded-full bg-kilted-green" style={{ width: `${(v / max) * 100}%` }} /></div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function RequestsTab() {
  const [reqs, setReqs] = useState(null);
  const load = () => api.get("/admin/account-requests").then((r) => setReqs(r.data));
  useEffect(() => { load(); }, []);
  const approve = async (r) => {
    if (!window.confirm(`Approve ${r.trade_name} for a 14-day account? They'll switch to monthly billing.`)) return;
    try { const { data } = await api.post(`/admin/account-requests/${r.id}/approve`); toast.success(data.email_sent ? "Approved — customer emailed" : "Approved (email couldn't be sent)"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const reject = async (r) => {
    const reasons = window.prompt("What's missing or wrong? One point per line — this goes in the email asking them to resubmit:");
    if (reasons === null) return;
    try { const { data } = await api.post(`/admin/account-requests/${r.id}/reject`, { reasons }); toast.success(data.email_sent ? "Rejected — resubmit email sent" : "Rejected (email couldn't be sent)"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  if (!reqs) return <Loading />;
  if (!reqs.length) return <p className="mt-6 text-sm text-muted-foreground" data-testid="requests-empty">No account applications yet.</p>;
  const badge = { pending: "bg-amber-100 text-amber-800", approved: "bg-emerald-100 text-emerald-800", rejected: "bg-red-100 text-red-700" };
  return (
    <div className="mt-4 space-y-3">
      {reqs.map((r) => (
        <Card key={r.id} data-testid={`request-card-${r.id}`}>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="font-heading text-base font-bold text-kilted-green">{r.trade_name} <span className={`ml-2 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${badge[r.status]}`}>{r.status}</span></p>
                <p className="text-xs text-muted-foreground">{r.user_name} · {r.user_email} · applied {r.created_at.slice(0, 10)}{r.decided_by ? ` · decided by ${r.decided_by}` : ""}</p>
              </div>
              {r.status === "pending" && (
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => approve(r)} data-testid={`approve-request-${r.id}`} className="bg-kilted-green hover:bg-kilted-greenLight">Approve</Button>
                  <Button size="sm" variant="outline" onClick={() => reject(r)} data-testid={`reject-request-${r.id}`} className="border-red-300 text-red-600 hover:bg-red-50">Reject</Button>
                </div>
              )}
            </div>
            <div className="mt-3 grid gap-x-6 gap-y-1 text-xs sm:grid-cols-2 lg:grid-cols-3">
              {[["ABN", r.abn], ["ACN", r.acn], ["Director(s)", r.director_names], ["Phone", r.phone],
                ["Mailing address", r.mailing_address], ["Goods/services", r.goods_services],
                ["Accounts contact", [r.accounts_name, r.accounts_phone, r.accounts_email].filter(Boolean).join(" · ")],
                ["Bank", [r.bank, r.branch, r.bsb, r.account_number].filter(Boolean).join(" · ")],
                ["Delivery", [r.delivery_contact, r.delivery_phone, r.delivery_address].filter(Boolean).join(" · ")],
                ["Signed", r.signed_name]].filter(([, v]) => v).map(([k, v]) => (
                <p key={k}><span className="text-muted-foreground">{k}:</span> <span className="font-medium">{v}</span></p>
              ))}
              {r.reject_reasons && <p className="text-red-600 sm:col-span-2">Rejected: {r.reject_reasons}</p>}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function Loading() { return <div className="mt-10 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>; }
function StripeSummaryTab() {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => { setData(null); api.get("/admin/finance/stripe-summary", { params: { month } }).then((r) => setData(r.data)).catch(() => setData(null)); }, [month]);
  useEffect(() => { load(); }, [load]);

  const download = async (fmt) => {
    setBusy(true);
    try {
      const res = await api.get("/admin/finance/stripe-summary/export", { params: { month, format: fmt }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `stripe-summary-${month}.${fmt}`; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); } finally { setBusy(false); }
  };

  const t = data?.totals;
  return (
    <Card className="mt-4" data-testid="stripe-summary-tab">
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between space-y-0">
        <div>
          <CardTitle className="text-lg">Stripe sales summary</CardTitle>
          <p className="mt-1 text-sm text-muted-foreground">Prepaid card takings for the month — for reconciling Stripe payouts in Xero (no invoice needed).</p>
        </div>
        <div className="flex items-center gap-2">
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="stripe-month-input" className="w-40" />
          <Button variant="outline" onClick={() => download("csv")} disabled={busy || !data} data-testid="stripe-export-csv" className="gap-1.5"><FileSpreadsheet className="h-4 w-4" /> CSV</Button>
          <Button variant="outline" onClick={() => download("pdf")} disabled={busy || !data} data-testid="stripe-export-pdf" className="gap-1.5"><FileText className="h-4 w-4" /> PDF</Button>
        </div>
      </CardHeader>
      <CardContent>
        {!data ? <Loading /> : (
          <>
            <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-5" data-testid="stripe-totals">
              <Metric label="Ex-GST" value={t.ex_gst} />
              <Metric label="GST collected" value={t.gst} />
              <Metric label="Gross" value={t.gross} accent />
              <Metric label="Est. fees" value={t.est_stripe_fees} />
              <Metric label="Est. net payout" value={t.est_net_payout} />
            </div>
            {data.count === 0 ? <p className="text-sm text-muted-foreground">No Stripe payments in {month}.</p> : (
              <Table>
                <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Tracking #</TableHead><TableHead>Customer</TableHead><TableHead className="text-right">Ex GST</TableHead><TableHead className="text-right">GST</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
                <TableBody>
                  {data.lines.map((ln) => (
                    <TableRow key={ln.tracking_number} data-testid={`stripe-row-${ln.tracking_number}`}>
                      <TableCell>{ln.date}</TableCell>
                      <TableCell className="font-medium">{ln.tracking_number}</TableCell>
                      <TableCell>{ln.customer_name}</TableCell>
                      <TableCell className="text-right">${ln.ex_gst.toFixed(2)}</TableCell>
                      <TableCell className="text-right">${ln.gst.toFixed(2)}</TableCell>
                      <TableCell className="text-right font-semibold">${ln.total.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <p className="mt-3 text-xs text-muted-foreground">{data.fee_note}</p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Metric({ label, value, accent, plain }) {
  return (
    <div className={`rounded-lg border p-3 ${accent ? "border-kilted-green/30 bg-kilted-green/5" : "border-border"}`}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`font-heading text-lg font-extrabold ${accent ? "text-kilted-green" : "text-foreground"}`}>{plain ? (value ?? 0) : `$${(value ?? 0).toFixed(2)}`}</p>
    </div>
  );
}

function ReferralsTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/referrals/leaderboard").then((r) => setData(r.data)).catch(() => setData(false)); }, []);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Referral leaderboard</CardTitle>
        {data && data !== false && (
          <p className="text-sm text-muted-foreground">Who's spreading the word — ${data.reward_amount.toFixed(0)} per successful referral, friends get ${data.welcome_amount.toFixed(0)} welcome credit.</p>
        )}
      </CardHeader>
      <CardContent>
        {data === null ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : data === false || !data.leaderboard.length ? (
          <p className="text-sm text-muted-foreground" data-testid="referrals-empty">No referral activity yet — invites and rewards will show up here.</p>
        ) : (
          <Table data-testid="referrals-leaderboard-table">
            <TableHeader>
              <TableRow>
                <TableHead>#</TableHead><TableHead>Customer</TableHead><TableHead>Email</TableHead>
                <TableHead className="text-right">Invites sent</TableHead><TableHead className="text-right">Sign-ups</TableHead>
                <TableHead className="text-right">Rewards</TableHead><TableHead className="text-right">Credit earned</TableHead>
                <TableHead className="text-right">Current balance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.leaderboard.map((r, i) => (
                <TableRow key={r.user_id} data-testid={`referral-row-${r.user_id}`}>
                  <TableCell className="font-semibold">{i + 1}</TableCell>
                  <TableCell className="font-medium">{r.name}</TableCell>
                  <TableCell className="text-muted-foreground">{r.email}</TableCell>
                  <TableCell className="text-right">{r.invites}</TableCell>
                  <TableCell className="text-right">{r.signups}</TableCell>
                  <TableCell className="text-right">{r.rewards}</TableCell>
                  <TableCell className="text-right font-semibold text-kilted-green">${r.earned.toFixed(2)}</TableCell>
                  <TableCell className="text-right">${r.credit_balance.toFixed(2)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
